import { Box } from '@material-ui/core';
import { t } from '@lingui/macro';
import { filter, get } from 'lodash';
import { i18n } from '@lingui/core';
import { useEffect, useState } from 'react';
import LinearProgress from '@material-ui/core/LinearProgress';
import AdminPage from '../AdminPage';
import { AdminPages, buildPageId } from '../AdminLinkUtils';
import FormSection from '../../form/FormSection';
import { useDetailStyles } from './LicenseStyles';
import { useLicense } from './LicenseHelper';
import { NgpLabelValue } from '../../form/NgpLabelValue';
import { useDataService } from '../../../services/ApiDataService';
import {
    AGENT_2K_USING_3K_FIELD,
    AGENT_3K_USING_3K_FIELD,
    COUNTS_FORMATTED_FIELD,
    EVALUATION_LICENSE_FIELD,
    FORMATTED_EXPIRATION_DATE_FIELD,
    IS_OVER_LIMIT_FIELD,
    LICENSE_2K_OBJECT,
    LICENSE_3K_OBJECT,
    LICENSE_CURRENT_COUNT_FIELD,
    LICENSE_TOTAL_COUNT_FIELD,
    LICENSE_290_OBJECT,
    LICENSE_490_OBJECT,
    LICENSE_795_OBJECT,
    LICENSE_AGENT_2K_HEADER_TEXT,
    LICENSE_AGENT_3K_HEADER_TEXT,
    LICENSE_COLLECTOR_HEADER_TEXT,
    LICENSE_COLLECTOR_OBJECT,
    LICENSE_DISABLED_COUNT_FORMAT_FIELD,
    LICENSE_SEM_OBJECT,
    LICENSE_SERVER_HEADER_TEXT,
    LICENSE_SERVER_OBJECT,
    LICENSE_TYPE_FIELD,
    SHOW_FAKE_COLLECTOR_FIELD,
    SHOW_MONITORED_ELEMENTS_FIELD,
    SINGLE_SEM_FIELD,
} from './LicenseConstants';
import { useIsIpmDisabled } from '../../../stores/EnvironmentStore';
import useUiAlerts, { createAlertConfig, WARNING } from '../../../stores/AlertsStore';

const functionName = AdminPages.Licensing;

const agentEdgeSensor290HeaderText = t`Edge Sensor 290 Licenses`;
const agentEdgeSensor490HeaderText = t`Edge Sensor 490/69x Licenses`;
const agentEdgeSensor795HeaderText = t`Edge Sensor 795 Licenses`;
const semHeaderText = t`Smart Edge Monitoring`;
const licenseTypeText = t`License Type`;
const collectorsText = t`Collectors`;
const monitoredElementsText = t`Monitored Elements`;
const expirationDateText = t`Expiration`;
const unlicensedText = t`Unlicensed`;
const agent2kText = t`nPoint 2000`;
const agent3kText = t`nPoint 3000H/V`;
const edgeAdaptersText = t`Edge Adapters`;
const fakeCollectorCount = '1 / 1';
const noLicenseHelpHeadingText = t`License Information`;
const noLicenseHelpText = t`No valid license found. Log into the console and run LicenseCL.sh to add your license.`;
const genericAlertText = t`One or more of the below licenses is over the limit.  Please contact your NETSCOUT Account Manager.`;
const singleSEMText = t`Single Smart Edge Monitoring`;
const SINGLE_SEM_OBJECT_FIELD = `${LICENSE_SEM_OBJECT}.${SINGLE_SEM_FIELD}`;
const totalLicensesText = t`License Count`;
const licensesUsedText = t`Licenses Used`;

export default function LicenseDetail() {
    const classes = useDetailStyles();
    const breadcrumbs = [{ text: i18n._(t`Licensing`) }];
    const [isIpmDisabled] = useIsIpmDisabled();
    const [, uiAlertActions] = useUiAlerts();
    const dataService = useDataService();
    const [es490s, set490s] = useState();
    const [es690s, set690s] = useState();
    const [es695s, set695s] = useState();

    const { getLicense, isLoading: finding, license: licenseData } = useLicense();
    useEffect(() => {
        function showAlert(license) {
            if (filter(license, (licenseItem) => licenseItem[IS_OVER_LIMIT_FIELD]).length > 0) {
                uiAlertActions.createAlert(createAlertConfig(WARNING, genericAlertText));
            }
        }

        getLicense().then(showAlert);
    }, [getLicense, uiAlertActions]);

    useEffect(() => {
        (async () => {
            const params = { limit: 10000, properties: ['name', 'type', 'state', 'edgeSensorModel'] };
            const agentData = await dataService.get('/api/v3/npoints/list', params);
            const np490s = agentData.data?.filter(
                (agents) =>
                    agents?.type.toLowerCase() === 'np-edge-h' &&
                    agents?.edgeSensorModel.toLowerCase() === '490' &&
                    agents?.state.toLowerCase() !== 'disabled' &&
                    agents?.state.toLowerCase() !== 'offline'
            ).length;
            const np690s = agentData.data?.filter(
                (agents) =>
                    agents?.type.toLowerCase() === 'np-edge-h' &&
                    agents?.edgeSensorModel.toLowerCase() === '690' &&
                    agents?.state.toLowerCase() !== 'disabled' &&
                    agents?.state.toLowerCase() !== 'offline'
            ).length;
            const np695s = agentData.data?.filter(
                (agents) =>
                    agents?.type.toLowerCase() === 'np-edge-h' &&
                    agents?.edgeSensorModel.toLowerCase() === '695' &&
                    agents?.state.toLowerCase() !== 'disabled' &&
                    agents?.state.toLowerCase() !== 'offline'
            ).length;

            set490s(np490s);
            set690s(np690s);
            set695s(np695s);
        })();
    }, [dataService]);
    const es490UsedText = t`(${es490s} ES-490)`;
    const es690UsedText = t`(${es690s} ES-690)`;
    const es695UsedText = t`(${es695s} ES-695)`;

    return (
        <AdminPage breadcrumbs={breadcrumbs} pageId={buildPageId(functionName)} actions={<></>}>
            <>
                {finding && <LinearProgress />}
                <Box ml={3} mt={2} className="scroll-container">
                    {!finding && !licenseData.validLicenseFound && (
                        <>
                            <FormSection title={noLicenseHelpHeadingText}>{noLicenseHelpText}</FormSection>
                        </>
                    )}

                    {!finding && licenseData.validLicenseFound && (
                        <>
                            {!get(licenseData, SINGLE_SEM_OBJECT_FIELD) && (
                                <FormSection title={LICENSE_SERVER_HEADER_TEXT}>
                                    <NgpLabelValue
                                        dataLabel={licenseTypeText}
                                        dataValue={get(licenseData, `${LICENSE_SERVER_OBJECT}.${LICENSE_TYPE_FIELD}`)}
                                    />

                                    {get(
                                        licenseData,
                                        `${LICENSE_SERVER_OBJECT}.${SHOW_FAKE_COLLECTOR_FIELD}`,
                                        false
                                    ) && <NgpLabelValue dataLabel={collectorsText} dataValue={fakeCollectorCount} />}

                                    {get(
                                        licenseData,
                                        `${LICENSE_SERVER_OBJECT}.${SHOW_MONITORED_ELEMENTS_FIELD}`,
                                        false
                                    ) && (
                                        <NgpLabelValue
                                            dataLabel={monitoredElementsText}
                                            dataValueClassName={
                                                get(licenseData, `${LICENSE_SERVER_OBJECT}.${IS_OVER_LIMIT_FIELD}`)
                                                    ? classes.licenseWarning
                                                    : ''
                                            }
                                            dataValue={get(
                                                licenseData,
                                                `${LICENSE_SERVER_OBJECT}.${COUNTS_FORMATTED_FIELD}`,
                                                false
                                            )}
                                        />
                                    )}

                                    {get(
                                        licenseData,
                                        `${LICENSE_SERVER_OBJECT}.${EVALUATION_LICENSE_FIELD}`,
                                        false
                                    ) && (
                                        <NgpLabelValue
                                            dataLabel={expirationDateText}
                                            dataValue={get(
                                                licenseData,
                                                `${LICENSE_SERVER_OBJECT}.${FORMATTED_EXPIRATION_DATE_FIELD}`,
                                                false
                                            )}
                                        />
                                    )}
                                </FormSection>
                            )}

                            {!isIpmDisabled && !get(licenseData, SINGLE_SEM_OBJECT_FIELD) && (
                                <FormSection title={LICENSE_COLLECTOR_HEADER_TEXT}>
                                    <NgpLabelValue
                                        dataLabel={licenseTypeText}
                                        dataValue={get(
                                            licenseData,
                                            `${LICENSE_COLLECTOR_OBJECT}.${LICENSE_TYPE_FIELD}`
                                        )}
                                    />

                                    {get(licenseData, `${LICENSE_COLLECTOR_OBJECT}.${COUNTS_FORMATTED_FIELD}`) && (
                                        <NgpLabelValue
                                            dataLabel={collectorsText}
                                            dataValueClassName={
                                                get(licenseData, `${LICENSE_COLLECTOR_OBJECT}.${IS_OVER_LIMIT_FIELD}`)
                                                    ? classes.licenseWarning
                                                    : ''
                                            }
                                            dataValue={get(
                                                licenseData,
                                                `${LICENSE_COLLECTOR_OBJECT}.${COUNTS_FORMATTED_FIELD}`
                                            )}
                                        />
                                    )}

                                    {get(
                                        licenseData,
                                        `${LICENSE_COLLECTOR_OBJECT}.${EVALUATION_LICENSE_FIELD}`,
                                        false
                                    ) && (
                                        <NgpLabelValue
                                            dataLabel={expirationDateText}
                                            dataValue={get(
                                                licenseData,
                                                `${LICENSE_COLLECTOR_OBJECT}.${FORMATTED_EXPIRATION_DATE_FIELD}`,
                                                false
                                            )}
                                        />
                                    )}
                                </FormSection>
                            )}

                            <FormSection title={LICENSE_AGENT_2K_HEADER_TEXT}>
                                <NgpLabelValue
                                    dataLabel={licenseTypeText}
                                    dataValue={get(licenseData, `${LICENSE_2K_OBJECT}.${LICENSE_TYPE_FIELD}`)}
                                />

                                {get(licenseData, `${LICENSE_2K_OBJECT}.${COUNTS_FORMATTED_FIELD}`) && (
                                    <NgpLabelValue
                                        dataLabel={agent2kText}
                                        dataValueClassName={
                                            get(licenseData, `${LICENSE_2K_OBJECT}.${IS_OVER_LIMIT_FIELD}`)
                                                ? classes.licenseWarning
                                                : ''
                                        }
                                        dataValue={get(licenseData, `${LICENSE_2K_OBJECT}.${COUNTS_FORMATTED_FIELD}`)}
                                    />
                                )}

                                {get(licenseData, `${LICENSE_2K_OBJECT}.${LICENSE_DISABLED_COUNT_FORMAT_FIELD}`) && (
                                    <NgpLabelValue
                                        dataLabel={unlicensedText}
                                        dataValueClassName={classes.licenseWarning}
                                        dataValue={get(
                                            licenseData,
                                            `${LICENSE_2K_OBJECT}.${LICENSE_DISABLED_COUNT_FORMAT_FIELD}`
                                        )}
                                    />
                                )}

                                {get(licenseData, `${LICENSE_2K_OBJECT}.${EVALUATION_LICENSE_FIELD}`, false) && (
                                    <NgpLabelValue
                                        dataLabel={expirationDateText}
                                        dataValue={get(
                                            licenseData,
                                            `${LICENSE_2K_OBJECT}.${FORMATTED_EXPIRATION_DATE_FIELD}`,
                                            false
                                        )}
                                    />
                                )}
                            </FormSection>

                            <FormSection title={LICENSE_AGENT_3K_HEADER_TEXT}>
                                <NgpLabelValue
                                    dataLabel={licenseTypeText}
                                    dataValue={get(licenseData, `${LICENSE_3K_OBJECT}.${LICENSE_TYPE_FIELD}`)}
                                />

                                <NgpLabelValue
                                    dataLabel={totalLicensesText}
                                    dataValue={
                                        get(licenseData, `${LICENSE_3K_OBJECT}.${LICENSE_TOTAL_COUNT_FIELD}`) || `0`
                                    }
                                />

                                {get(licenseData, `${LICENSE_3K_OBJECT}.${COUNTS_FORMATTED_FIELD}`) && (
                                    <NgpLabelValue
                                        dataLabel={agent3kText}
                                        dataValueClassName={
                                            get(licenseData, `${LICENSE_3K_OBJECT}.${IS_OVER_LIMIT_FIELD}`)
                                                ? classes.licenseWarning
                                                : ''
                                        }
                                        dataValue={`${get(
                                            licenseData,
                                            `${LICENSE_3K_OBJECT}.${LICENSE_CURRENT_COUNT_FIELD}`
                                        )}`}
                                    />
                                )}

                                {get(licenseData, `${LICENSE_3K_OBJECT}.${LICENSE_DISABLED_COUNT_FORMAT_FIELD}`) && (
                                    <NgpLabelValue
                                        dataLabel={unlicensedText}
                                        dataValueClassName={classes.licenseWarning}
                                        dataValue={get(
                                            licenseData,
                                            `${LICENSE_3K_OBJECT}.${LICENSE_DISABLED_COUNT_FORMAT_FIELD}`
                                        )}
                                    />
                                )}

                                {get(licenseData, `${LICENSE_3K_OBJECT}.${AGENT_2K_USING_3K_FIELD}`) && (
                                    <NgpLabelValue
                                        dataLabel=""
                                        dataValue={get(licenseData, `${LICENSE_3K_OBJECT}.${AGENT_2K_USING_3K_FIELD}`)}
                                    />
                                )}

                                {get(licenseData, `${LICENSE_3K_OBJECT}.${AGENT_3K_USING_3K_FIELD}`) && (
                                    <NgpLabelValue
                                        dataLabel=""
                                        dataValue={get(licenseData, `${LICENSE_3K_OBJECT}.${AGENT_3K_USING_3K_FIELD}`)}
                                    />
                                )}

                                {get(licenseData, `${LICENSE_3K_OBJECT}.${EVALUATION_LICENSE_FIELD}`, false) && (
                                    <NgpLabelValue
                                        dataLabel={expirationDateText}
                                        dataValue={get(
                                            licenseData,
                                            `${LICENSE_3K_OBJECT}.${FORMATTED_EXPIRATION_DATE_FIELD}`,
                                            false
                                        )}
                                    />
                                )}
                            </FormSection>

                            <FormSection title={agentEdgeSensor290HeaderText}>
                                <NgpLabelValue
                                    dataLabel={licenseTypeText}
                                    dataValue={get(licenseData, `${LICENSE_290_OBJECT}.${LICENSE_TYPE_FIELD}`)}
                                />

                                <NgpLabelValue
                                    dataLabel={totalLicensesText}
                                    dataValue={
                                        get(licenseData, `${LICENSE_290_OBJECT}.${LICENSE_TOTAL_COUNT_FIELD}`) || `0`
                                    }
                                />

                                <NgpLabelValue
                                    dataLabel={licensesUsedText}
                                    dataValueClassName={
                                        get(licenseData, `${LICENSE_290_OBJECT}.${IS_OVER_LIMIT_FIELD}`)
                                            ? classes.licenseWarning
                                            : ''
                                    }
                                    dataValue={`${get(
                                        licenseData,
                                        `${LICENSE_290_OBJECT}.${LICENSE_CURRENT_COUNT_FIELD}`
                                    )}`}
                                />

                                {get(licenseData, `${LICENSE_290_OBJECT}.${LICENSE_DISABLED_COUNT_FORMAT_FIELD}`) && (
                                    <NgpLabelValue
                                        dataLabel={unlicensedText}
                                        dataValueClassName={classes.licenseWarning}
                                        dataValue={get(
                                            licenseData,
                                            `${LICENSE_290_OBJECT}.${LICENSE_DISABLED_COUNT_FORMAT_FIELD}`
                                        )}
                                    />
                                )}

                                {get(licenseData, `${LICENSE_290_OBJECT}.${EVALUATION_LICENSE_FIELD}`, false) && (
                                    <NgpLabelValue
                                        dataLabel={expirationDateText}
                                        dataValue={get(
                                            licenseData,
                                            `${LICENSE_290_OBJECT}.${FORMATTED_EXPIRATION_DATE_FIELD}`,
                                            false
                                        )}
                                    />
                                )}
                            </FormSection>

                            <FormSection title={agentEdgeSensor490HeaderText}>
                                <NgpLabelValue
                                    dataLabel={licenseTypeText}
                                    dataValue={get(licenseData, `${LICENSE_490_OBJECT}.${LICENSE_TYPE_FIELD}`)}
                                />

                                <NgpLabelValue
                                    dataLabel={totalLicensesText}
                                    dataValue={
                                        get(licenseData, `${LICENSE_490_OBJECT}.${LICENSE_TOTAL_COUNT_FIELD}`) || `0`
                                    }
                                />

                                <NgpLabelValue
                                    dataLabel={licensesUsedText}
                                    dataValueClassName={
                                        get(licenseData, `${LICENSE_490_OBJECT}.${IS_OVER_LIMIT_FIELD}`)
                                            ? classes.licenseWarning
                                            : ''
                                    }
                                    dataValue={`${get(
                                        licenseData,
                                        `${LICENSE_490_OBJECT}.${LICENSE_CURRENT_COUNT_FIELD}`
                                    )}`}
                                />

                                <NgpLabelValue
                                    dataLabel={''}
                                    dataValueClassName={
                                        get(licenseData, `${LICENSE_490_OBJECT}.${IS_OVER_LIMIT_FIELD}`)
                                            ? classes.licenseWarning
                                            : ''
                                    }
                                    dataValue={`${es490UsedText}`}
                                />

                                <NgpLabelValue
                                    dataLabel={''}
                                    dataValueClassName={
                                        get(licenseData, `${LICENSE_490_OBJECT}.${IS_OVER_LIMIT_FIELD}`)
                                            ? classes.licenseWarning
                                            : ''
                                    }
                                    dataValue={`${es690UsedText}`}
                                />

                                <NgpLabelValue
                                    dataLabel={''}
                                    dataValueClassName={
                                        get(licenseData, `${LICENSE_490_OBJECT}.${IS_OVER_LIMIT_FIELD}`)
                                            ? classes.licenseWarning
                                            : ''
                                    }
                                    dataValue={`${es695UsedText}`}
                                />

                                {get(licenseData, `${LICENSE_490_OBJECT}.${LICENSE_DISABLED_COUNT_FORMAT_FIELD}`) && (
                                    <NgpLabelValue
                                        dataLabel={unlicensedText}
                                        dataValueClassName={classes.licenseWarning}
                                        dataValue={get(
                                            licenseData,
                                            `${LICENSE_490_OBJECT}.${LICENSE_DISABLED_COUNT_FORMAT_FIELD}`
                                        )}
                                    />
                                )}

                                {get(licenseData, `${LICENSE_490_OBJECT}.${EVALUATION_LICENSE_FIELD}`, false) && (
                                    <NgpLabelValue
                                        dataLabel={expirationDateText}
                                        dataValue={get(
                                            licenseData,
                                            `${LICENSE_490_OBJECT}.${FORMATTED_EXPIRATION_DATE_FIELD}`,
                                            false
                                        )}
                                    />
                                )}
                            </FormSection>

                            <FormSection title={agentEdgeSensor795HeaderText}>
                                <NgpLabelValue
                                    dataLabel={licenseTypeText}
                                    dataValue={get(licenseData, `${LICENSE_795_OBJECT}.${LICENSE_TYPE_FIELD}`)}
                                />

                                <NgpLabelValue
                                    dataLabel={totalLicensesText}
                                    dataValue={
                                        get(licenseData, `${LICENSE_795_OBJECT}.${LICENSE_TOTAL_COUNT_FIELD}`) || `0`
                                    }
                                />

                                <NgpLabelValue
                                    dataLabel={licensesUsedText}
                                    dataValueClassName={
                                        get(licenseData, `${LICENSE_795_OBJECT}.${IS_OVER_LIMIT_FIELD}`)
                                            ? classes.licenseWarning
                                            : ''
                                    }
                                    dataValue={`${get(
                                        licenseData,
                                        `${LICENSE_795_OBJECT}.${LICENSE_CURRENT_COUNT_FIELD}`
                                    )}`}
                                />

                                {get(licenseData, `${LICENSE_795_OBJECT}.${LICENSE_DISABLED_COUNT_FORMAT_FIELD}`) && (
                                    <NgpLabelValue
                                        dataLabel={unlicensedText}
                                        dataValueClassName={classes.licenseWarning}
                                        dataValue={get(
                                            licenseData,
                                            `${LICENSE_795_OBJECT}.${LICENSE_DISABLED_COUNT_FORMAT_FIELD}`
                                        )}
                                    />
                                )}

                                {get(licenseData, `${LICENSE_795_OBJECT}.${EVALUATION_LICENSE_FIELD}`, false) && (
                                    <NgpLabelValue
                                        dataLabel={expirationDateText}
                                        dataValue={get(
                                            licenseData,
                                            `${LICENSE_795_OBJECT}.${FORMATTED_EXPIRATION_DATE_FIELD}`,
                                            false
                                        )}
                                    />
                                )}
                            </FormSection>

                            <FormSection title={semHeaderText}>
                                {get(licenseData, `${LICENSE_SEM_OBJECT}.${COUNTS_FORMATTED_FIELD}`) && (
                                    <NgpLabelValue
                                        dataLabel={edgeAdaptersText}
                                        dataValueClassName={
                                            get(licenseData, `${LICENSE_SEM_OBJECT}.${IS_OVER_LIMIT_FIELD}`)
                                                ? classes.licenseWarning
                                                : ''
                                        }
                                        dataValue={get(
                                            licenseData,
                                            `${LICENSE_SEM_OBJECT}.${COUNTS_FORMATTED_FIELD}`,
                                            '0 / 0'
                                        )}
                                    />
                                )}
                                {get(licenseData, SINGLE_SEM_OBJECT_FIELD) && (
                                    <NgpLabelValue
                                        dataLabel={singleSEMText}
                                        dataValueClassName={classes.singleSEM}
                                        dataValue={get(licenseData, SINGLE_SEM_OBJECT_FIELD) ? 'Enabled' : ''}
                                    />
                                )}
                            </FormSection>
                        </>
                    )}
                </Box>
            </>
        </AdminPage>
    );
}
